import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from '@/components/feature/SEO';
import { getArticleBySlug } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <Layout>
        <div className="pt-20">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-6">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    image: article.coverImage || undefined,
    publisher: {
      '@type': 'Organization',
      name: '91精品影视',
    },
  };

  return (
    <Layout>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalPath={`/article/${article.slug}`}
        ogType="article"
        schemaObjects={[articleSchema]}
      />

      <div className="pt-20 pb-4 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: '/category' },
              { label: article.title },
            ]}
          />
        </div>
      </div>

      <article className="w-full py-6 md:py-10 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          <div className="prose-custom">
            <div
              className="text-foreground-800 leading-relaxed text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </article>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid oklch(var(--background-200));
        }
        .prose-custom p {
          margin-bottom: 1rem;
          line-height: 1.8;
          color: oklch(var(--foreground-800));
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.1rem;
          font-weight: 600;
          color: oklch(var(--foreground-850));
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </Layout>
  );
}